import React, { useState, useEffect } from "react";

import {
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormGroup,
  Rating,
  IconButton,
  Box,
  Typography,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DeleteIcon from "@mui/icons-material/Delete";

import TextFieldComponent from "./FormFields/TextField";
import TextAreaField from "./FormFields/TextAreaField";
import RadioField from "./FormFields/RadioField";
import CheckboxField from "./FormFields/CheckboxField";
import RatingField from "./FormFields/RatingField";
import FileField from "./FormFields/FileField";
import NumberField from "./FormFields/NumberField";
import DateField from "./FormFields/DateField";
import PhoneField from "./FormFields/PhoneField";
import PictureField from "./FormFields/PictureField";

import Cookies from "js-cookie";

import "./UserForm.css";

const UserForm = ({
  templates,
  customerKey,
  brandId,
  batchId,
  templateName,
  setDrawerOpen,
  onClose,
  setShowSuccessNotification,
}) => {
  console.log(
    "Configured user form with customized templates has been received",
    templates
  );

  const cookieKey = `formData_${customerKey}_${brandId}_${batchId}_${templateName}`;
  const autoSaveCookieKey = `autoSaveFormData_${customerKey}_${brandId}_${batchId}_${templateName}`;
  const photoCaptureLabel = "Capture Photo";
  const [s3Urls, setS3Urls] = useState({});

  const [formData, setFormData] = useState(() => {
    const initialData = {};
    if (templates && Array.isArray(templates)) {
      templates.forEach((field) => {
        if (field.type === "checkbox") {
          initialData[field.label] = [];
        } else if (field.type === "radio") {
          initialData[field.label] = "";
        } else if (field.type === "picture") {
          initialData[field.label] = [];
        } else {
          initialData[field.label] = "";
        }
      });
    }
    initialData[photoCaptureLabel] = [];
    console.log("Initial form data: ", initialData);
    return initialData;
  });

  // const [isSubmitted, setIsSubmitted] = useState(() => { return Cookies.get(cookieKey) !== undefined });

  // a useEffect to load and save the form data to cookies when the form is opened or closed, not on initial load
  useEffect(() => {
    // if (isSubmitted) return;
    const autoSavedData = Cookies.get(autoSaveCookieKey);

    if (autoSavedData) {
      try {
        const parsedData = JSON.parse(autoSavedData);
        if (JSON.stringify(formData) !== JSON.stringify(parsedData)) {
          saveFormDataToCookies(formData);
        }
      } catch (error) {
        console.debug("Error comparing form data with saved data: ", error);
      }
    } else {
      saveFormDataToCookies(formData);
    }
  }, [formData, autoSaveCookieKey]);

  let BASE_URL =
    process.env.REACT_APP_CONFIG_API_URL?.trim() ||
    "https://devapi.fiduciaai.in";

  // a function to handle the change in the form
  const handleChange = (label, type, value) => {
    // if (isSubmitted) return;

    setFormData((prev) => {
      let newData;

      if (type === "checkbox") {
        const currentValues = prev[label] || [];
        const updatedValues = currentValues.includes(value)
          ? currentValues.filter((item) => item !== value)
          : [...currentValues, value];
        newData = { ...prev, [label]: updatedValues };
      } else if (
        type === "picture" &&
        (label === photoCaptureLabel ||
          templates.find((f) => f.label === label)?.type === "picture")
      ) {
        // const currentPhotos = prev[label] || [];
        const currentPhotos = Array.isArray(prev[label]) ? prev[label] : [];
        newData = { ...prev, [label]: [...currentPhotos, value] };

        uploadFileToS3(value, label, currentPhotos.length)
          .then((s3Url) => {
            setS3Urls((prev) => ({
              ...prev,
              [label]: [...(prev[label] || []), s3Url],
            }));
          })
          .catch((error) => {
            console.error("Error uploading file to S3: ", error);
          });
      } else if (type === "file") {
        newData = { ...prev, [label]: value };
        uploadFileToS3(value, label)
          .then((s3Url) => {
            setS3Urls((prev) => ({
              ...prev,
              [label]: s3Url,
            }));
          })
          .catch((error) => {
            console.error("Error uploading file to S3: ", error);
          });
      } else {
        newData = { ...prev, [label]: value };
      }
      saveFormDataToCookies(newData);

      return newData;
    });
  };

  // a function to handle the removal of a photo
  const handleRemoveSpecificPhoto = (label, index) => {
    // if (isSubmitted) return;

    setFormData((prev) => {
      const currentPhotos = prev[label] || [];
      const updatedPhotos = currentPhotos.filter((_, i) => i !== index);
      const newData = { ...prev, [label]: updatedPhotos };

      const s3UrlToDelete = s3Urls[label]?.[index];
      if (s3UrlToDelete) {
        fetch(BASE_URL + "/api/deleteAWSFile", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ url: s3UrlToDelete }),
        }).catch((error) => {
          console.error("Error deleting file from S3: ", error);
        });

        setS3Urls((prev) => ({
          ...prev,
          [label]: prev[label]?.filter((_, i) => i !== index) || [],
        }));
      }
      saveFormDataToCookies(newData);
      return newData;
    });
  };

  // a function to handle the removal of a file
  const handleRemoveFile = (label) => {
    // if (isSubmitted) return;

    setFormData((prev) => {
      const newData = { ...prev, [label]: null };

      const s3UrlToDelete = s3Urls[label];

      if (s3UrlToDelete) {
        fetch(BASE_URL + "/api/deleteAWSFile", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ url: s3UrlToDelete }),
        }).catch((error) => {
          console.error("Error deleting file from S3: ", error);
        });

        setS3Urls((prev) => {
          const newS3Urls = { ...prev };
          delete newS3Urls[label];
          return newS3Urls;
        });
      }
      saveFormDataToCookies(newData);
      return newData;
    });
  };

  // a function to save the form data to cookies when the form is closed
  const saveFormDataToCookies = (data) => {
    try {
      Cookies.set(autoSaveCookieKey, JSON.stringify(data), { expires: 30 });
      console.debug("Form data auto saved to Cookies");
    } catch (error) {
      console.error("Error auto saving form data to cookies: ", error);
    }
  };

  console.debug("S3 URLs: ", s3Urls);

  const handleSubmit = async (event) => {
    event.preventDefault();

    console.debug("Submitting form data: ", formData);
    // Cookies.set(cookieKey, JSON.stringify(formData), { expires: 30 });
    // Cookies.remove(autoSaveCookieKey);
    // setIsSubmitted(true);

    const templateId = templates.length > 0 ? templates[0].templateId : null;
    if (!templateId) {
      console.error("No template ID found in the templates array");
      return;
    }

    try {
      const finalFormData = {};

      for (const [label, value] of Object.entries(formData)) {
        if (
          (label === photoCaptureLabel ||
            templates.find((f) => f.label === label)?.type === "picture") &&
          Array.isArray(value)
        ) {
          finalFormData[label] = s3Urls[label] || [];
        } else if (value instanceof File) {
          finalFormData[label] = s3Urls[label] || null;
        } else {
          finalFormData[label] = value;
        }
      }

      const requestBody = {
        customerKey: customerKey,
        brandId: brandId,
        batchId: batchId,
        templateId: templateId.toString(),
        templatename: templateName,
        templatedata: JSON.stringify(finalFormData),
        iscoupon: false,
      };

      const response = await fetch(
        // "https://cloudbee.fiducia-ai.com/cebackendapi/saveTemplateData",
        BASE_URL + "/api/saveTemplateData",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
            // Accept: "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      console.debug("Form submission successful: ", responseData);

      if (response.status === 200) {
        // close the overlay first and then show the success notification
        if (onClose) {
          onClose();
        }

        // setTimeout(()=>{
        //   if(setShowSuccessNotification) {
        //     setShowSuccessNotification(true);
        //   }
        // }, 500);
        if (setShowSuccessNotification) {
          setShowSuccessNotification(true);
        }

        Cookies.remove(autoSaveCookieKey);
      }
    } catch (error) {
      console.error("Error Submitting Form Data: ", error);
    }
  };

  const uploadFileToS3 = async (file, fieldLabel, index = null) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("customerkey", customerKey);
      formData.append("brandid", brandId);
      formData.append("batchid", batchId);
      // if index is null, set the index to 0, otherwise the actual index, index=null for file upload & corresponding index of the photo for photo uploads
      formData.append("index", index === null ? 0 : index);
      formData.append("images", "");

      const response = await fetch(BASE_URL + "/api/uploadWebImage", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! Status: ${response.status} ${response.statusText} ${response.body}`
        );
      }

      const url = await response.text();
      return url;
    } catch (error) {
      console.error("Error uploading file to S3: ", error);
      throw error;
    }
  };

  // Cleanup object URLs when component unmounts or photos change
  useEffect(() => {
    return () => {
      // Revoke all object URLs to prevent memory leaks
      if (
        formData[photoCaptureLabel] &&
        Array.isArray(formData[photoCaptureLabel])
      ) {
        formData[photoCaptureLabel].forEach((photo) => {
          if (photo && typeof photo === "object" && photo.name && photo.size) {
            try {
              // We can't revoke URLs we created in render, but we can track them
              // This is mainly for cleanup when component unmounts
            } catch (error) {
              console.warn("Error during photo cleanup:", error);
            }
          }
        });
      }
    };
  }, [formData[photoCaptureLabel]]);

  const renderField = (field) => {
    const { tfid, label, type, value } = field;

    const commonProps = {
      key: tfid,
      label,
      value: formData[label] || "",
      onChange: (value) => handleChange(label, type, value),
      required: true,
    };

    switch (type.toLowerCase()) {
      case "text":
        return <TextFieldComponent {...commonProps} />;
      case "textarea":
        return <TextAreaField {...commonProps} />;
      case "radio":
        return <RadioField {...commonProps} options={value} />;
      case "checkbox":
        return <CheckboxField {...commonProps} options={value} />;
      case "rating":
        return <RatingField {...commonProps} />;
      case "file":
        return (
          <FileField
            {...commonProps}
            tfid={tfid}
            onRemove={() => handleRemoveFile(label)}
          />
        );
      case "number":
        return <NumberField {...commonProps} />;
      case "date":
        return <DateField {...commonProps} />;
      case "phone":
        return <PhoneField {...commonProps} />;
      case "picture":
        return (
          <PictureField
            {...commonProps}
            tfid={tfid}
            onRemovePhoto={(index) => handleRemoveSpecificPhoto(label, index)}
            photoCaptureLabel={photoCaptureLabel}
          />
        );
      default:
        console.warn(`Unknown field type : ${type}`);
    }
  };

  if (!templates || !Array.isArray(templates) || templates.length === 0) {
    console.warn("No templates provided to UserForm component");
    return null;
  }

  return (
    <form className="user-form" onSubmit={handleSubmit}>
      <div className="form-fields-container">
        {templates.map((field) => renderField(field))}

        <div className="form-actions">
          <Button
            variant="outlined"
            type="submit"
            sx={{
              borderRadius: "8px",
              padding: "10px 20px",
              color: "white",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: "1px solid #6e7885",
              fontWeight: "bold",
              width: "100%",
              "&:hover": {
                borderColor: "white",
              },
            }}
          >
            Submit
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UserForm;
